import { createTheme } from '@mui/material';
import { grey } from '@mui/material/colors';

const FontWeight = {
  bold: 700,
  semiBold: 600,
  medium: 500,
  light: 300,
};
export const customsProColors = {
  GreenTint: '#F8FAF7',
  lightGreen: '#DCECDB',
  brightGreen: '#5AA845',
  customsProGreen: '#104447',
  channelPortGreen: '#4A7951',
};

export const primaryColor = {
  100: '#EDF5F9',
  200: '#D2EAF4',
  600: '#1981AD',
  700: '#044561',
  900: '#022A3B',
};
export const successColor = {
  100: '#ECF8F2',
  200: '#DAF2E5',
  600: '#50CD89',
  700: '#47BE7D',
};
export const warningColor = {
  100: '#FFF5E7',
  200: '#FFE8C7',
  600: '#FFA621',
  700: '#F69B11',
};
export const errorColor = {
  100: '#FFF5F8',
  200: '#FFD6E2',
  600: '#F1416C',
  700: '#A20C31',
};
export const moonstoneColor = {
  50: '#F6FDFE',
  100: '#E8FAFC',
  200: '#B4E9EE',
  600: '#00A0AF',
  700: '#005D66',
};
export const amethystColor = {
  50: '#FAF6FE',
  100: '#F2E8FC',
  200: '#D9BEF4',
  600: '#A761EA',
  700: '#6016A7',
};
export const sunsetColor = {
  50: '#FEF7EB',
  100: '#FEF1DD',
  200: '#FBDAA7',
  600: '#FEC876',
  700: '#CB7B01',
};
export const ecruColor = {
  50: '#FCFBF8',
  100: '#F8F4EC',
  200: '#E8DCC9',
  600: '#CDAD7A',
  700: '#826331',
};
export const coralColor = {
  50: '#FFF6F5',
  100: '#FFE9E6',
  200: '#FDBAAF',
  600: '#FE9382',
  700: '#AB1E07',
};
export const denimColor = {
  50: '#F6F8FE',
  100: '#E7EEFD',
  200: '#B6C3E2',
  600: '#4461A8',
  700: '#2C3F6D',
};
export const greyColor = {
  50: '#FBFBFC',
  100: '#F6F6F9',
  200: '#EFF2F5',
  300: '#E4E6EF',
  400: '#B5B5C3',
  500: '#A1A5B7',
  600: '#7E8299',
  700: '#5E6278',
  800: '#3F4254',
  900: '#181C32',
};

export const muiGlobalTheme = createTheme({
  palette: {
    primary: {
      main: primaryColor[600],
      light: primaryColor[200],
      lighter: primaryColor[100],
      dark: primaryColor[700],
      dark2: primaryColor[900],
    },
    secondary: {
      main: '#2C23BC',
      light: '#EAE9F6',
      dark: '#090090',
    },
    warning: {
      main: warningColor[600],
      light: warningColor[200],
      lighter: warningColor[100],
      dark: warningColor[700],
    },
    info: {
      main: primaryColor[600],
      light: primaryColor[200],
      lighter: primaryColor[100],
      dark: primaryColor[700],
    },
    success: {
      main: successColor[600],
      light: successColor[200],
      lighter: successColor[100],
      dark: successColor[700],
      contrastText: 'white',
    },
    error: {
      main: errorColor[600],
      light: errorColor[200],
      lighter: errorColor[100],
      dark: errorColor[700],
    },
    errorLight: {
      main: errorColor[100],
      light: errorColor[100],
      dark: errorColor[100],
      contrastText: errorColor[600],
    },
    alternative: {
      main: greyColor[600],
      light: greyColor[600],
      dark: greyColor[600],
    },
    moonstone: {
      main: moonstoneColor[600],
      light: moonstoneColor[100],
      lighter: moonstoneColor[50],
      dark: moonstoneColor[700],
    },
    denim: {
      main: denimColor[600],
      light: denimColor[100],
      lighter: denimColor[50],
      dark: denimColor[700],
    },
    amethyst: {
      main: amethystColor[600],
      light: amethystColor[100],
      lighter: amethystColor[50],
      dark: amethystColor[700],
    },
    sunset: {
      main: sunsetColor[600],
      light: sunsetColor[100],
      lighter: sunsetColor[50],
      dark: sunsetColor[700],
    },
    ecru: {
      main: ecruColor[600],
      light: ecruColor[100],
      lighter: ecruColor[50],
      dark: ecruColor[700],
    },
    coral: {
      main: coralColor[600],
      light: coralColor[100],
      lighter: coralColor[50],
      dark: coralColor[700],
    },
    black: {
      light: '#000',
      main: '#000',
      dark: '#000',
    },
    white: {
      light: '#fff',
      main: '#fff',
      dark: '#fff',
    },
    primaryDark: {
      main: primaryColor[700],
      light: '#82A2B0',
      dark: '#022E45',
      contrastText: '#FFF',
    },
    primaryLight: {
      main: primaryColor[100],
      light: primaryColor[100],
      dark: primaryColor[100],
      contrastText: primaryColor[600],
    },
    secondaryGrey: {
      main: greyColor[100],
      light: '#82A2B0',
      dark: '#022E45',
      contrastText: greyColor[800],
    },
    grey: {
      '50': greyColor[50],
      '100': greyColor[100],
      '200': greyColor[200],
      '300': greyColor[300],
      '400': greyColor[400],
      '500': greyColor[500],
      '600': greyColor[600],
      '700': greyColor[700],
      '800': greyColor[800],
      '900': greyColor[900],
    },
    text: {
      primary: greyColor[800],
      disabled: greyColor[500],
    },
    background: {
      default: grey[100],
    },
  },
  typography: {
    display1: {
      fontSize: '2.875rem',
      fontWeight: FontWeight.bold,
    },
    display2: {
      fontSize: '2rem',
      fontWeight: FontWeight.bold,
    },
    h1: {
      fontSize: '1.875rem',
      fontWeight: FontWeight.semiBold,
    },
    h2: {
      fontSize: '1.25rem',
      fontWeight: FontWeight.semiBold,
    },
    h3: {
      fontSize: '1.125rem',
      fontWeight: FontWeight.semiBold,
    },
    h4: {
      fontSize: '.875rem',
      fontWeight: FontWeight.bold,
    },
    bodyXLBold: {
      fontSize: '1.125rem',
      fontWeight: FontWeight.bold,
    },
    bodyXL: {
      fontSize: '1.125rem',
      fontWeight: FontWeight.medium,
    },
    bodyLG: {
      fontSize: '1rem',
      fontWeight: FontWeight.semiBold,
    },
    bodyLGMedium: {
      fontSize: '1rem',
      fontWeight: FontWeight.medium,
    },
    bodyLGBold: {
      fontSize: '1rem',
      fontWeight: FontWeight.bold,
    },
    bodyMD: {
      fontSize: '0.875rem',
    },
    bodyMDMedium: {
      fontSize: '0.875rem',
      fontWeight: FontWeight.medium,
    },
    body1: {
      fontSize: '0.875rem',
    },
    bodySM: {
      fontSize: '0.75rem',
      color: '#7E8299',
    },
    bodySMMedium: {
      fontSize: '0.75rem',
      fontWeight: FontWeight.medium,
    },
    bodyXS: {
      fontSize: '0.625rem',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: FontWeight.bold,
    },
    overline: {
      fontSize: '0.875rem',
      fontWeight: FontWeight.medium,
    },
    fontWeightBold: FontWeight.bold,
    fontWeightSemiBold: FontWeight.semiBold,
    fontWeightMedium: FontWeight.medium,
    fontWeightLight: FontWeight.light,
    fontFamily: [
      'Poppins',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiBadge: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.MuiBadge-colorError': {
            backgroundColor: theme.palette?.error?.dark,
          },
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginBottom: theme.spacing(2),
        }),
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.bodySM.fontSize,
          '&,&.Mui-error,&.Mui-disabled': {
            color: theme.palette.grey[700],
          },
        }),
        asterisk: ({ theme }) => ({
          color: theme.palette.error.main,
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.grey[800],
          fontSize: theme.typography.bodyMD.fontSize,
          background: 'white',
          height: 40,
          '&.Mui-disabled': {
            backgroundColor: theme.palette.grey[200],
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: `${theme.palette.grey[300]} !important`,
            },
          },
          '&.MuiInputBase-sizeSmall': {
            height: 36,
            lineHeight: 'initial',
            '.MuiIconButton-edgeEnd ': {
              color: theme.palette.grey[500],
              '.MuiSvgIcon-root': {
                height: theme.typography.bodyLG.fontSize,
                width: theme.typography.bodyLG.fontSize,
              },
            },
            '&.MuiInputBase-multiline': { height: 'initial' },
          },
          '&.MuiInputBase-multiline': {
            height: 'auto',
            paddingTop: muiGlobalTheme.spacing(2),
            paddingBottom: muiGlobalTheme.spacing(2),
          },
        }),
        inputSizeSmall: ({ theme }) => ({
          fontSize: theme.typography.bodySM.fontSize,
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.grey[300],
          '.border': { fill: '#fff', strokeWidht: 1, stroke: 'currentColor' },
          '&.Mui-checked.Mui-disabled': { color: `${theme.palette.primary.main} !important`, opacity: 0.3 },
          '&.Mui-disabled': {
            color: `${theme.palette.grey[300]} !important`,
            '.border': {
              fill: 'currentColor',
            },
          },
        }),
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionStart: ({ theme }) => ({
          color: theme.palette.grey[500],
          '.MuiSvgIcon-root': {
            fontSize: theme.typography.bodyLG.fontSize,
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: ({ theme }) => ({
          width: theme.typography.bodyLG.fontSize,
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          fontWeight: 'initial',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        colorInherit: ({ theme }) => ({
          color: theme.palette.grey[500],
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          lineHeight: 'initial',
          textTransform: 'initial',
          fontWeight: FontWeight.medium,
          // '&.MuiButton-contained': {
          //   boxShadow: 'none',
          // },
          '&.MuiButton-containedSuccess:hover': {
            backgroundColor: theme.palette.success.main,
          },
          '&.Mui-disabled': {
            opacity: 0.4,
          },
          '&.MuiButton-containedPrimaryDark:hover,&.MuiButton-containedPrimaryDark:active': {
            backgroundColor: theme.palette.primaryDark.main,
          },

          // '&.MuiButton-containedError:hover,&.MuiButton-containedError:active': {
          //   backgroundColor: theme.palette.error.main,
          // },
          // '&.MuiButton-containedPrimary:hover,&.MuiButton-containedPrimary:active': {
          // },
          // '&.MuiButton-textWhite:hover,&.MuiButton-textWhite:active': {
          //   backgroundColor: theme.palette.grey[800],
          // },

          '&:hover,&:active,&.MuiButton-containedSecondaryGrey': {
            backgroundColor: theme.palette.secondaryGrey.main,
          },
          '&.MuiButton-containedAlternative': {
            backgroundColor: theme.palette.grey[100],
          },
        }),
        startIcon: ({ theme }) => ({
          marginRight: theme.spacing(0.5),
          display: 'flex',
        }),
        contained: {
          '&,&:hover': {
            boxShadow: 'none',
          },
        },

        containedPrimary: ({ theme }) => ({
          '&:hover,&:active,&.Mui-disabled': {
            color: 'white',
            backgroundColor: theme.palette.primary.main,
          },
        }),
        containedSuccess: ({ theme }) => ({
          '&:hover,&:active,&.Mui-disabled': {
            color: 'white',
            backgroundColor: theme.palette.success.main,
          },
        }),

        containedError: ({ theme }) => ({
          '&:hover,&:active,&.Mui-disabled': {
            color: 'white',
            backgroundColor: theme.palette.error.main,
          },
        }),
        sizeSmall: {
          height: 36,
          '.MuiSvgIcon-fontSizeMedium': {
            fontSize: 'inherit',
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          '.MuiCircularProgress-circle': {
            strokeLinecap: 'round',
            strokeWidth: 6,
            r: 18,
          },
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-disabled .MuiStepConnector-line': {
            borderTopStyle: 'dashed',
          },
          '&.Mui-active .MuiStepConnector-line': {
            borderColor: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.Mui-disabled &': {
            color: theme.palette.grey[300],
            '.MuiStepIcon-text': {
              fill: theme.palette.grey[600],
            },
          },
        }),
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.MuiStepLabel-label': {
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.palette.text.disabled,
          },
        }),
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.MuiSvgIcon-fontSizeSmall': {
            fontSize: theme.typography.body2.fontSize,
          },
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          margin: theme.spacing(3, 0),
          '&.MuiDivider-textAlignLeft': {
            '&::before': {
              display: 'none',
            },
            '.MuiDivider-wrapper': {
              color: theme.palette.grey[500],
            },
          },
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: 42,
          height: 28,
          padding: 0,
          display: 'flex',
          '& .MuiSwitch-switchBase': {
            padding: 3,
            '&.Mui-checked': {
              transform: 'translateX(14px)',
              '.MuiSwitch-thumb': {
                color: '#fff',
              },
              '& + .MuiSwitch-track': {
                opacity: 1,
              },
            },
          },
          '& .MuiSwitch-thumb': {
            boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
            width: 22,
            height: 22,
            borderRadius: 11,
            transition: theme.transitions.create(['width'], {
              duration: 200,
            }),
          },
          '& .MuiSwitch-track': {
            borderRadius: 28 / 2,
            backgroundColor: theme.palette.grey[300],
            boxSizing: 'border-box',
          },
          '.MuiSwitch-switchBase.Mui-checked.Mui-disabled+.MuiSwitch-track': {
            opacity: 0.3,
          },
        }),
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.MuiTableCell-head': {
            fontSize: theme.typography.bodySM.fontSize,
            color: theme.palette.grey[400],
            lineHeight: '120%',
            backgroundColor: theme.palette.background.paper,
          },
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          '.MuiTableBody-root &:nth-of-type(odd)': {
            backgroundColor: theme.palette.grey[50],
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          '[role="tablist"]': {
            gap: theme.spacing(1),
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'initial',
          fontSize: theme.typography.bodyMD.fontSize,
          '&.Mui-selected': {
            color: theme.palette.grey[800],
          },
          ':not(.Mui-selected)': {
            fontWeight: 'initial',
            color: theme.palette.grey[600],
          },
          '&.Mui-disabled': {
            opacity: 0.5,
          },
          '&.MuiButtonBase-root': { padding: theme.spacing(0.5, 1) },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&::before': {
            top: 'initial',
            bottom: 0,
          },
          '&:last-child': {
            '&::before': {
              backgroundColor: 'transparent',
            },
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-expanded': {
            minHeight: 'initial',
          },
          '& .MuiAccordionSummary-content': {
            '&,&.Mui-expanded': {
              margin: theme.spacing(1, 0),
            },
          },
        }),
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 0,
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.borderRadius,
          marginBottom: theme.spacing(2),
        }),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiPaper-root': {
            boxShadow: theme.shadows[5],
          },
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.severity === 'error' &&
            ownerState.variant === 'standard' && {
              border: `1px solid ${theme.palette.error.light}`,
              backgroundColor: theme.palette.error.lighter,
              color: theme.palette.error.dark,
              '& .MuiAlert-icon': {
                color: theme.palette.error.dark,
              },
            }),
          ...(ownerState.severity === 'warning' &&
            ownerState.variant === 'standard' && {
              border: `1px solid ${theme.palette.warning.light}`,
              backgroundColor: theme.palette.warning.lighter,
              color: theme.palette.warning.main,
              '& .MuiAlert-icon': {
                color: theme.palette.warning.main,
              },
            }),
          ...(ownerState.severity === 'info' &&
            ownerState.variant === 'standard' && {
              border: `1px solid ${theme.palette.info.light}`,
              backgroundColor: theme.palette.info.lighter,
              color: theme.palette.info.main,
              '& .MuiAlert-icon': {
                color: theme.palette.info.main,
              },
            }),
          ...(ownerState.severity === 'success' &&
            ownerState.variant === 'standard' && {
              border: `1px solid ${theme.palette.success.light}`,
              backgroundColor: theme.palette.success.lighter,
              color: theme.palette.success.main,
              '& .MuiAlert-icon': {
                color: theme.palette.success.main,
              },
            }),
        }),
      },
    },
  },
});
